<template>
  <v-main>

    <v-container
        id="claim-container"
        fluid
        tag="section"
    >

        <base-material-card
            icon="mdi-family-tree"
            class="px-5 py-3"
            ref="form"
        >

          <div data-cy="page-messages">
            <shared-page-messages :error-messages="errorMessages"
                                  :warning-messages="warningMessages"
                                  :success-messages="successMessages">
            </shared-page-messages>

            <p v-if="!computedClaimUpdatable">
              This Claim has been processed and cannot be updated.
            </p>
            <p>Please only submit one Claim per occurrence.
              You will receive email and SMS notifications when your Claim is processed.</p>
          </div>

          <v-form ref="claimForm"
                  v-model="valid"
                  @submit.prevent="validate"
                  lazy-validation
                  :disabled="!computedClaimUpdatable"
          >

            <v-row>
              <v-col
                  cols="12"
                  sm="6"
              >
                <v-select
                    v-if="computedDependentUpdatable"
                    v-model="computedDependent"
                    label="Dependent"
                    id="dependent"
                    :items="dependentList"
                    item-text="names"
                    item-value="id"
                    return-object
                    :rules="rules.required"
                ></v-select>
                <v-text-field
                    v-else
                    label="Dependent"
                    v-model="claim.dependent.names"
                  ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                  cols="12"
                  sm="9"
              >
                <v-text-field
                    v-model="claim.cause"
                    label="Cause of Death"
                    id="cause"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>

            </v-row>

            <v-row v-if="claimId && claim.verifiedBy">
              <v-col
                  cols="12"
                  sm="9"
              >
                <v-text-field
                    v-model="claim.verifiedBy.names"
                    label="Verified By"
                    disabled
                ></v-text-field>
              </v-col>

            </v-row>

            <v-row v-if="claimId">
              <v-col
                  cols="12"
                  sm="9"
              >
                <v-textarea
                    v-model="claim.notes"
                    label="Notes"
                    rows="1"
                    disabled
                ></v-textarea>
              </v-col>

            </v-row>


            <v-row>
              <v-col
                  cols="12"
                  sm="3"
              >
                <v-text-field
                    v-model="computedIncidentDate"
                    label="Incident Date"
                    id="incident-date"
                    type="date"
                    :rules="rules.required"
                ></v-text-field>
              </v-col>

            </v-row>

            <v-row v-if="claimId">
              <v-col
                  cols="12"
                  sm="3"
              >
                <v-text-field
                    v-model="computedPaymentDate"
                    label="Payment Date"
                    disabled
                    type="date"
                ></v-text-field>
              </v-col>

              <v-col
                  cols="12"
                  sm="3"
              >
                <v-text-field
                    v-model="claim.paymentAmount"
                    label="Payment Amount"
                    disabled
                ></v-text-field>
              </v-col>

              <v-col
                  cols="12"
                  sm="3"
              >
                <v-text-field
                    v-model="claim.paymentMethod"
                    label="Payment Method"
                    disabled
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row v-if="claimId">
              <v-col
                  cols="12"
                  sm="3"
              >
                <v-text-field
                    v-model="claimStatusMap[claim.status]"
                    label="Claim Status"
                    disabled
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                  cols="12"
                  sm="4"
              >
                <div class="my-2">
                  <v-btn
                      color="primary"
                      dark
                      type="submit"
                      id="submitClaim"
                      v-if="computedClaimUpdatable"
                  >
                    Submit
                  </v-btn>
                </div>
              </v-col>
            </v-row>

          </v-form>


        </base-material-card>

    </v-container>

  </v-main>
</template>

<script>
  import api from '@/services/api'
  import {mapGetters} from 'vuex'
  import validation from '@/utils/validation'
  import geoUtils from '@/utils/geography'
  import constants from '@/utils/constants'
  import moment from 'moment'
  import SharedPageMessages from '@/views/components/shared/PageMessages.vue'

  export default {
    name: 'CoreClaimView',
    components: {SharedPageMessages},

    created() {
      this.userId = this.getUserId
      this.claimId = this.$route.params.id
      this.statesList = geoUtils.fetchStates()
      this.countriesList = geoUtils.fetchCountries()
      this.claimStatusMap = constants.getClaimStatusMap()

      this.fetchDependents()
      if (this.claimId) {
        this.fetchClaim()
      }
    },

    computed: {
      ...mapGetters({
        getUserId: 'getUserId'
      }),
      computedCountry: {
        get() {
          return geoUtils.findCountryByCode(this.claim.country)
        },
        set(value) {
          this.claim.country = value
        }
      },
      computedDependent: {
        get() {
          if (this.claim.dependent === undefined) {
            return
          }
          return this.dependentList.find(dep => this.claim.dependent.id === dep.id)
        },
        set(value) {
          this.claim.dependent = value
        }
      },
      dependentList: {
        get() {
          return this.dependents
              .filter(dep => !(this.claim.id === undefined && dep.status !== 'APPROVED'))
              .map(dep => ({ id: dep.id, names: `${dep.names} (${dep.relationType.name})` }))
        }
      },
      computedIncidentDate: {
        get() {
          return moment(this.claim.incidentDate).format('YYYY-MM-DD')
        },
        set(value) {
          this.claim.incidentDate = value
        }
      },
      computedDependentUpdatable() {
        if (this.claim.dependent === undefined || this.claim.dependent === null) {
          return true
        }
        return this.claim.dependent.status !== 'DECEASED'
      },
      computedClaimUpdatable() {
        return !['VERIFIED', 'PAID', 'REJECTED'].includes(this.claim.status)
      },
      computedPaymentDate: {
        get() {
          if (this.claim.paymentDate == null) {
            return
          }
          return moment(this.claim.paymentDate).format('YYYY-MM-DD')
        },
        set(value) {
          this.claim.paymentDate = value
        }
      }
    },

    methods: {
      validate() {
        this.clearMessages()
        if(this.$refs.claimForm.validate()) {
          if (this.claim.id !== undefined && this.claim.id !== null) {
            return this.updateClaim()
          }
          this.addClaim()
        }
      },
      clearMessages() {
        this.errorMessages = []
        this.warningMessages = []
        this.successMessages = []
      },
      fetchDependents() {
        api
            .fetchDependents(this.userId)
            .then(response => {
              this.dependents = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      fetchClaim() {
        api
            .fetchClaim(this.userId, this.claimId)
            .then(response => {
              this.claim = response.data
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      addClaim() {
        this.$gtag.event('submit_claim')

        let payload = {
          dependentId: this.claim.dependent.id,
          incidentDate: moment(this.claim.incidentDate).format('YYYY-MM-DD'),
          cause: this.claim.cause
        }

        api.addClaim(this.userId, payload)
            .then(response => {
              if (response.status === 201) {
                this.$router.push(`/claims/view`)
              }
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      updateClaim() {
        this.$gtag.event('update_claim')

        let payload = {
          dependentId: this.claim.dependent.id,
          incidentDate: this.claim.incidentDate,
          cause: this.claim.cause
        }
        api
            .updateClaim(this.userId, this.claimId, payload)
            .then(response => {
              if (response.status === 200) {
                this.$router.push('/claims/view')
              }
            })
            .catch(err => {
              this.handleError(err)
            })
      },
      handleError(err) {
        let errorMessage = api.parseResponseError(err)
        this.errorMessages.push(errorMessage)
      }
    },

    data() {
      return {
        valid: true,
        errorMessages: [],
        successMessages: [],
        warningMessages: [],
        userId: null,
        claimId: null,
        user: {},
        claim: {
          id: null,
          dependent: {},
          status: null,
          verifiedBy: {},
          paymentDate: null,
          amountDue: null,
          paymentMethod: null,
          notes: null
        },
        dependents: [],
        claimStatusMap: {},
        genderOptions: [
          'Male',
          'Female',
          'Other'
        ],
        relationTypes: [],
        countriesList: [],
        rules: {
          required: [
            v => !!v || 'required'
          ],
          numeric: [
            v => validation.isInteger(v) || 'must be a number'
          ],
          birthYear: [
            v => validation.isInteger(v) || 'must be a year number',
            v => validation.isMoreThan(v, 1900) || 'must be after 1900',
            v => validation.isBeforeOrEqualToCurrentYear(v) || 'must be before or equal to current year'
          ]
        }
      }
    }
  }
</script>
